/**
 * THREE.LOD builder from a render asset's variant chain (Graphics G2).
 *
 * Reads the `render_asset_variant` LOD chain (cooked by G2: LOD0/LOD1/LOD2), loads
 * each level's GLB via AssetModule, and wraps them in a `THREE.LOD` whose switch
 * distances derive from each variant's `screen_size_threshold` × the model's bounding
 * radius. The RenderSub system calls `lod.update(camera)` to swap by distance.
 */
import * as THREE from 'three/webgpu';
import type { AssetModule } from '../modules/AssetModule.js';
import { activePreset } from './quality_tiers.js';

export interface LODHandle { object: THREE.LOD; release: () => void; }

export async function buildLOD(assets: AssetModule, renderAssetId: bigint): Promise<LODHandle> {
  const variants = assets.variants(renderAssetId);
  const lod = new THREE.LOD();
  const usedLods: number[] = [];
  let base: THREE.Object3D | null = null;
  // G5: the active quality tier scales the absolute switch distance — a lower tier swaps to the
  // cheaper LOD sooner (smaller scale → shorter distance), cutting triangle load on weak GPUs.
  // distance = radius × tier.lodDistanceScale ÷ screen_size_threshold.
  const preset = activePreset();
  const scale = preset.lodDistanceScale;
  // forceLowLod (potato): never load LOD0 at all — its 20 MB GLB + 16×2048²
  // texture chain is the profiled city-walk freeze, and the tier's renderer
  // never earns the detail back. LOD1 becomes the close-up level (distance 0).
  const levels = preset.settlementForceLowLod && variants.some((v) => v.lod >= 1)
    ? variants.filter((v) => v.lod >= 1)
    : variants;

  if (levels.length === 0) {
    base = await assets.load(renderAssetId); // refcount LOD0 (no cooked chain)
    lod.addLevel(base.clone(), 0);
  } else {
    let radius = 1;
    for (const [i, v] of levels.entries()) {
      let src: THREE.Object3D;
      // A level failing mid-chain (MinIO double-drop under load) must not leak
      // the refs already acquired: keep the partial chain if we have ANY level,
      // else rethrow with nothing held. The far levels self-heal on rebuild.
      try {
        if (v.lod === 0) { base = await assets.load(renderAssetId); src = base; }
        else { src = await assets.loadVariant(renderAssetId, v.lod); usedLods.push(v.lod); }
      } catch (e) {
        if (i === 0) throw e; // nothing acquired yet — propagate
        break;
      }
      if (i === 0) radius = boundingRadius(src);
      const distance = i === 0 ? 0 : (radius * scale) / Math.max(0.05, v.screenSizeThreshold);
      lod.addLevel(src.clone(), distance);
    }
  }

  return {
    object: lod,
    release: () => { if (base) assets.dispose(renderAssetId); for (const l of usedLods) assets.disposeVariant(renderAssetId, l); },
  };
}

function boundingRadius(obj: THREE.Object3D): number {
  const box = new THREE.Box3().setFromObject(obj);
  const s = new THREE.Vector3();
  box.getSize(s);
  return Math.max(s.x, s.y, s.z, 1) * 0.5;
}
